// client/src/coms/serveradmin/ServerEventsView.js
// A server's upcoming events for members (server menu → Events): times in your own time zone, RSVP, who is
// going (when the event shows attendees), and a jump to the event's channel.
import React, { useState } from 'react';
import '../organisation/org.css';
import './admin.css';
import { comsApiPost } from '../comsApi';
import { Chip, Empty, Flash, useAction, useLoad, fmtDate } from './adminKit';

const KIND = { voice: '🎙️ Voice event', game_night: '🎲 Game night', watch: '🍿 Watch Together', meeting: '🗣️ Meeting', competition: '🏆 Competition', creator: '🎨 Creator event', other: '📅 Event' };

export default function ServerEventsView({ server, onClose, onManage }) {
  const serverKey = String((server && (server.id || server.public_id)) || '');
  const { data, err, reload } = useLoad(serverKey ? `/servers/${encodeURIComponent(serverKey)}/events` : '');
  const act = useAction();
  const rsvp = (id, status) => act.run(() => comsApiPost(`/servers/${encodeURIComponent(serverKey)}/events/${id}/rsvp`, { status })).then(reload);
  return (
    <div className="vxa-root" data-testid="server-events">
      <div className="vxo-head">
        <div className="vxo-head-title">
          <h1>Events</h1>
          <span className="vxo-head-sub">{(server && server.name) || ''}</span>
        </div>
        <div className="vxa-row">
          {data && data.can_create && typeof onManage === 'function' ? (
            <button type="button" className="vx-btn" onClick={onManage}>
              Create & manage
            </button>
          ) : null}
          <button type="button" className="vx-btn" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
      <main className="vxo-main" style={{ overflowY: 'auto', padding: '16px 22px' }}>
        <Flash msg={act.msg} />
        {err ? (
          <Empty icon="📅" title="Events">{err}</Empty>
        ) : !data ? (
          <div className="vxo-loading">Loading…</div>
        ) : !data.events.length ? (
          <Empty icon="📅" title="Nothing planned yet">Events this server schedules — game nights, watch parties, meetings — appear here.</Empty>
        ) : (
          data.events.map((e) => {
            const d = new Date(e.start_ms);
            return (
              <div key={e.id} className="vxa-card">
                <div className="vxa-event">
                  <div className="vxa-event-date" aria-hidden="true">
                    <span>{d.toLocaleString(undefined, { month: 'short' })}</span>
                    <b>{d.getDate()}</b>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div>
                      <b>{e.title}</b> {e.status === 'live' ? <Chip kind="ok">Happening now</Chip> : null}
                    </div>
                    <div className="vxa-small vxa-muted">
                      {KIND[e.kind] || KIND.other} · {fmtDate(e.start_ms)}
                      {e.end_ms ? ` – ${fmtDate(e.end_ms)}` : ''}
                      {e.host_name ? ` · hosted by ${e.host_name}` : ''}
                      {e.location_text ? ` · ${e.location_text}` : ''}
                    </div>
                    {e.description ? <p className="vxa-small" style={{ whiteSpace: 'pre-wrap', margin: '6px 0' }}>{e.description}</p> : null}
                    <div className="vxa-row" style={{ marginTop: 6 }}>
                      {[
                        ['going', 'Going'],
                        ['interested', 'Interested'],
                        ['declined', 'Can’t make it'],
                      ].map(([s, l]) => (
                        <button key={s} type="button" className={`vx-btn small${e.my_rsvp === s ? ' is-on' : ''}`} aria-pressed={e.my_rsvp === s} disabled={act.busy} onClick={() => rsvp(e.id, e.my_rsvp === s ? 'none' : s)}>
                          {l}
                        </button>
                      ))}
                      <span className="vxa-small vxa-muted">
                        {e.counts.going} going · {e.counts.interested} interested
                      </span>
                      {e.channel ? (
                        <button
                          type="button"
                          className="vx-btn small"
                          onClick={() => window.dispatchEvent(new CustomEvent('vox-open-target', { detail: { kind: 'channel', server_key: serverKey, channel_id: e.channel.id, channel_kind: e.channel.kind } }))}
                        >
                          {e.channel.kind === 'voice' ? 'Go to voice channel' : 'Go to channel'}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </main>
    </div>
  );
}
